/*
  Переключатель вкладок кабинета.

  08.09.2026, решение владельца: экран отчётов стал слишком длинным - четыре
  раздела месячного отчёта, счётчики окупаемости и шесть метрик портфеля шли
  одной простынёй на три с половиной тысячи пикселей. Главное - деньги за
  период - оказывалось внизу, куда доезжали не все.

  Разметка соответствует роли `tablist`: читалка называет вкладку, говорит,
  выбрана ли она, и связывает её с содержимым. Клавиатура работает сама -
  вкладки остаются кнопками, а не ссылками с `href="#"`.

  Содержимое невыбранной вкладки НЕ рисуется: иначе её таблицы попадают в поиск
  по странице и в чтение с экрана, а человек их не видит.

  14.09.2026 появился второй ряд вкладок - внутри вкладки «Судебные участки»
  (указание владельца: «визуальное и физическое разграничение»). Два одинаковых
  ряда подряд человек читает как один сбившийся, поэтому у вложенного ряда свой
  вид: не подчёркивание снизу, а переключатель на подложке. Разметка и поведение
  при этом те же - вид задаётся `nested`, а не вторым компонентом.

  21.09.2026 у вкладки появился значок (`badge`) - тем же приёмом, что у пунктов
  меню. Содержимое невыбранной вкладки не рисуется, поэтому без значка человек
  не узнаёт, что за ней его ждёт работа: состав должников, ожидающий
  подтверждения, виден только тому, кто на вкладку зашёл. Значок обязан
  объяснять себя словами (`badgeTitle`), иначе число на экране - загадка.
*/

import { useEffect, useRef } from 'react'

export default function Tabs({ tabs, active, onChange, nested }) {
  const chosen = useRef(null)

  /*
    Выбранная вкладка подводится в видимую часть ряда (21.09.2026, осмотр на
    390). На узком экране ряд не переносится, а листается вбок, и четвёртая
    вкладка стоит за краем: открыв присланную ссылку `?tab=documents`, человек
    видел ряд, начинающийся с «Дело», и первую вкладку без подчёркивания -
    то есть экран, на котором ничего не выбрано.

    28.10.2026 (аудит 5.3, осмотр на 390): `scrollIntoView` с
    `block: 'nearest'` двигал не только ряд, но и страницу - у карточки счёта
    ряд вкладок лежит ниже первого экрана, и при открытии страница сама уезжала
    на 678 точек вниз: номер, стадия и долг уходили за верх. Поэтому листается
    только сам ряд (`scrollLeft`), а страница не трогается никогда.
  */
  useEffect(() => {
    const tab = chosen.current
    const row = tab?.parentElement
    if (!row) return
    const left = tab.getBoundingClientRect().left - row.getBoundingClientRect().left + row.scrollLeft
    const right = left + tab.offsetWidth
    if (left < row.scrollLeft) row.scrollLeft = left
    else if (right > row.scrollLeft + row.clientWidth) row.scrollLeft = right - row.clientWidth
  }, [active])

  /*
    Стрелки (аудит 28.10.2026, раздел 5.4): по правилам разметки вкладок ←/→ переходят между
    вкладками по кругу, Home и End - на первую и последнюю, а в ряд Tab заходит одной остановкой
    (на выбранной). Переключение вкладки остаётся за Enter и пробелом: панель при смене
    подгружает данные, и перебор стрелками гонял бы запросы.
  */
  const move = (event) => {
    const keys = tabs.map((tab) => tab.key)
    const at = keys.indexOf(document.activeElement?.id.replace(/^tab-/, ''))
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
    let next = null
    if (step && at >= 0) next = keys[(at + step + keys.length) % keys.length]
    else if (event.key === 'Home') next = keys[0]
    else if (event.key === 'End') next = keys[keys.length - 1]
    if (next === null) return
    event.preventDefault()
    document.getElementById(`tab-${next}`)?.focus()
  }
  // Если выбранной нет среди вкладок (адрес с чужим `?tab=`), в ряд входит первая.
  const entry = tabs.some((tab) => tab.key === active) ? active : tabs[0]?.key

  return (
    <div className={`tabs${nested ? ' tabs--nested' : ''}`} role="tablist" onKeyDown={move}>
      {tabs.map((tab) => (
        <button
          key={tab.key}
          ref={tab.key === active ? chosen : null}
          type="button"
          role="tab"
          id={`tab-${tab.key}`}
          tabIndex={tab.key === entry ? 0 : -1}
          aria-selected={tab.key === active}
          aria-controls={`panel-${tab.key}`}
          /*
            `no-hover-lift`: вкладка разметкой кнопка, но для человека это
            переключатель раздела, а не действие. Подъём и вдавливание делали бы
            вид, что на неё нажимают, и строка вкладок прыгала бы под курсором -
            то же исключение, что у своего поля выбора (скилл `frontend`, 6б).
          */
          className={`tabs__tab no-hover-lift${tab.key === active ? ' tabs__tab--active' : ''}`}
          onClick={() => onChange(tab.key)}
        >
          {tab.title}
          {tab.badge ? (
            <span className="tabs__badge" title={tab.badgeTitle}>
              {tab.badge}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  )
}

export function TabPanel({ tabKey, active, children }) {
  if (tabKey !== active) return null
  return (
    <div role="tabpanel" id={`panel-${tabKey}`} aria-labelledby={`tab-${tabKey}`}>
      {children}
    </div>
  )
}
